import React, { useEffect, useState } from "react";
import { flushSync } from "react-dom";

import styles from "./Table.module.css";
import TableSearch from "./TableSearch";
import { useTableManagerContext } from "../table-manager/TableManagerContext";

import sortArrow from "../icons/sort-arrow.svg";

const TableHead = ({
                       columns: directColumns,
                       records: directRecords,
                       selected,
                       handleSelectedChange,
                       tableHeaderProps,
                       columnResizeNeeded,
                       alreadySelectedRecords,
                       alreadySelectedIds,
                       isRecordSelectable = () => true,
                   }) => {
    const {
        columnsToDisplay = directColumns,
        records = directRecords,
        setInsertColumn,
        sortOptions,
        searchOptions,
        selectOptions,
    } = useTableManagerContext();

    const { sortable, onSortChange, config: sortConfig = {} } = sortOptions || {};
    const { selectable } = selectOptions || {};
    const {
        searchable,
        onSearchChange,
        searchConfig,
    } = searchOptions || {};

    const [columnWidths, setColumnWidths] = useState({});

    // Added useEffect logic to calculate widths (line 38 - 65);
    const DEFAULT_MIN_WIDTH_REM = 5;
    const rootFontSize =
        parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

    useEffect(() => {
        if (!columnsToDisplay || columnsToDisplay.length === 0) return;

        // compute only for accessors missing in state
        const missing = columnsToDisplay.filter(
            (c) => columnWidths[c.accessor] === undefined
        );
        if (missing.length === 0) return;

        let customColumnWidthsNeeded = false;
        const initialWidths = {};
        missing.forEach((item) => {
            if (typeof item.width === "number") {
                initialWidths[item.accessor] = item.width;
                customColumnWidthsNeeded = true;
            }
        });

        setColumnWidths((prev) => ({ ...prev, ...initialWidths }));
    }, [columnsToDisplay]);

    const selectableRecords = records.filter((record) => isRecordSelectable(record));
    const hasSelectableRecords = selectableRecords.length > 0;

    let hasAll = false;
    if (hasSelectableRecords && alreadySelectedRecords?.length) 
    {
        const allSelected = selectableRecords.every(record => {
            const recordId = alreadySelectedIds ? record[alreadySelectedIds.id] : null;
            return alreadySelectedRecords.some(id => String(id) === String(recordId));
        });
        hasAll = allSelected;
    }

    return (
        <thead className={styles.thead} {...tableHeaderProps}>
            <tr>
                {selectable && (
                    <th align="center" className={styles.check}>
                        {hasSelectableRecords && (
                            <input
                                id="all"
                                type="checkbox"
                                {...(alreadySelectedIds && alreadySelectedIds?.editable
                                    ? { checked: selected.all }
                                    : { checked: hasAll || selected.all })}
                                disabled={alreadySelectedIds && !alreadySelectedIds.editable ? hasAll : false}
                                onChange={handleSelectedChange}
                            />
                        )}
                    </th>
                )}

                {columnsToDisplay.map(
                    (
                        {
                            title,
                            accessor,
                            unsearchable: columnUnsearchable,
                            unsortable: columnUnsortable,
                            minWidth,
                            headerLines,
                        },
                        currentColumnIndex
                    ) => {
                        const { column, order } = sortConfig;
                        const searchValue = searchConfig?.find((item) => item.column === accessor)?.searchKey || "";

                        const getSortIconClassName = () => {
                            if (column === accessor)
                                return [
                                    styles.sortActive,
                                    order === "asc" && styles.sortAsc,
                                ].join(" ");
                            return "";
                        };

                        const getNextOrder = () => {
                            let nextOrder = "sort-asc";
                            if (order === "asc") nextOrder = "sort-desc";
                            else if (order === "desc") nextOrder = "un-sort";
                            return nextOrder;
                        };

                        const handleDragStart = (e) => {
                            e.dataTransfer.setData("columnIndex", currentColumnIndex);
                        };

                        const handleDrop = (e) => {
                            e.persist();
                            const insertColumn = {
                                columnIndex: Number(e.dataTransfer.getData("columnIndex")),
                                insertBefore: currentColumnIndex,
                            };
                            if (insertColumn.columnIndex === insertColumn.insertBefore)
                                return;
                            document.startViewTransition(() => {
                                flushSync(() => {
                                    setInsertColumn(insertColumn);
                                });
                            });
                        };

                        const handleDragEnd = (e) => {
                            setInsertColumn(null);
                        };

                        const handleResizeStart = (e, accessor, minWidth) => {
                            e.preventDefault();
                            const startX = e.clientX;
                            // Conversion from px to rem 
                            const startWidth =
                                columnWidths[accessor] ||
                                e.target.parentElement.offsetWidth / rootFontSize;

                            const onMouseMove = (moveEvent) => {
                                const newWidth =
                                    startWidth + (moveEvent.clientX - startX) / rootFontSize;
                                setColumnWidths((prev) => ({
                                    ...prev,
                                    [accessor]: Math.max(
                                        newWidth,
                                        minWidth || DEFAULT_MIN_WIDTH_REM
                                    ),
                                }));
                            };

                            const onMouseUp = () => {
                                document.removeEventListener("mousemove", onMouseMove);
                                document.removeEventListener("mouseup", onMouseUp);
                            };

                            document.addEventListener("mousemove", onMouseMove);
                            document.addEventListener("mouseup", onMouseUp);
                        };

                        return (
                            <th
                                key={title}
                                draggable={Boolean(setInsertColumn)}
                                onDragStart={handleDragStart}
                                onDrop={handleDrop}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                }}
                                onDragEnd={handleDragEnd}
                                style={{
                                    viewTransitionName: `${accessor}-transition`,
                                    position: "relative",
                                    width:
                                        columnWidths[accessor] &&
                                        typeof columnWidths[accessor] === "number"
                                            ? `${columnWidths[accessor]}rem`
                                            : "auto",
                                    ...(minWidth != null && typeof minWidth === "number"
                                        ? { minWidth: `${minWidth}rem` }
                                        : {}),
                                }}
                                className={setInsertColumn && styles.notDragging}
                            >
                                <div className={styles.headerCell}>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            sortable && !columnUnsortable && onSortChange(accessor)
                                        }
                                    >
                                        {/* clamp style for header text */}
                                        <span
                                            style={{
                                                color: "#666666",
                                                fontSize: "15px",
                                                fontWeight: "500",
                                                WebkitLineClamp: headerLines || 1,
                                            }}
                                            className={styles.title}
                                        >
                                            {title}
                                        </span>

                                        {sortable && !columnUnsortable && (
                                            <span data-title={getNextOrder()}>
                                                <img
                                                    src={sortArrow}
                                                    alt="sort"
                                                    className={[
                                                        styles.sortIcon,
                                                        getSortIconClassName(),
                                                    ].join(" ")}
                                                />
                                                <img
                                                    src={sortArrow}
                                                    alt="sort"
                                                    className={[
                                                        styles.sortIcon,
                                                        styles.sortAsc,
                                                        order !== "desc" && styles.notVisible,
                                                        getSortIconClassName(),
                                                    ].join(" ")}
                                                />
                                            </span>
                                        )}
                                    </button>

                                    {searchable && !columnUnsearchable && (
                                        <TableSearch
                                            onSearchChange={onSearchChange}
                                            column={accessor}
                                            focus={currentColumnIndex===0}
                                            searchValue={searchValue}
                                        />
                                    )}
                                </div>

                                {/* Resize handle */}
                                <div
                                    className={styles.resizeHandle}
                                    style={{
                                        borderRight:
                                            currentColumnIndex === columnsToDisplay.length - 1
                                                ? "1.5px #F1F4FB solid"
                                                : null,
                                        cursor: !columnResizeNeeded ? "" : "col-resize",
                                    }}
                                    onMouseDown={ columnResizeNeeded ? (e) => handleResizeStart(e, accessor, minWidth) : null}
                                />
                            </th>
                        );
                    }
                )}
            </tr>
        </thead>
    );
};

export default TableHead;
